import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';
import PageTransition from './components/PageTransition';
import Footer from './components/Footer';
import Home from './pages/Home';

// Route-level code-splitting: heavy secondary pages are loaded on-demand
const AnatomyDirectory = lazy(() => import('./pages/AnatomyDirectory'));
const OrganPage = lazy(() => import('./pages/OrganPage'));
const DiseasesDirectory = lazy(() => import('./pages/DiseasesDirectory'));
const DiseasePage = lazy(() => import('./pages/DiseasePage'));
const Calculators = lazy(() => import('./pages/Calculators'));
const BMICalculator = lazy(() => import('./pages/BMICalculator'));
const ASCVDCalculator = lazy(() => import('./pages/ASCVDCalculator'));
const CrClCalculator = lazy(() => import('./pages/CrClCalculator'));
const PharmaDirectory = lazy(() => import('./pages/PharmaDirectory'));
const DrugPage = lazy(() => import('./pages/DrugPage'));
const ArticlesDirectory = lazy(() => import('./pages/ArticlesDirectory'));
const ArticlePage = lazy(() => import('./pages/ArticlePage'));
const Quiz = lazy(() => import('./pages/Quiz'));
const Shop = lazy(() => import('./pages/Shop'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DoctorDashboard = lazy(() => import('./pages/DoctorDashboard'));
const LMS = lazy(() => import('./pages/Lms'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const Admin = lazy(() => import('./pages/Admin'));
import { Toaster } from './components/ui/toaster';
import './App.css';

function App() {
  useEffect(() => {
    document.title = 'Dr Pulse';
  }, []);

  return (
    <div className="App min-w-0 overflow-x-clip">
      <ThemeProvider>
        <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Navbar />
          <div className="min-h-[70vh] min-w-0 overflow-x-clip">
            <PageTransition>
              <Suspense fallback={
                <div className="flex items-center justify-center min-h-[50vh]">
                  <div className="w-8 h-8 rounded-full border-2 border-[#B40026] border-t-transparent animate-spin" />
                </div>
              }>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/anatomy" element={<AnatomyDirectory />} />
                  <Route path="/anatomy/:slug" element={<OrganPage />} />
                  <Route path="/diseases" element={<DiseasesDirectory />} />
                  <Route path="/diseases/:slug" element={<DiseasePage />} />
                  <Route path="/calculators" element={<Calculators />} />
                  <Route path="/calculators/bmi" element={<BMICalculator />} />
                  <Route path="/calculators/ascvd" element={<ASCVDCalculator />} />
                  <Route path="/calculators/crcl" element={<CrClCalculator />} />
                  <Route path="/pharma" element={<PharmaDirectory />} />
                  <Route path="/pharma/:slug" element={<DrugPage />} />
                  <Route path="/articles" element={<ArticlesDirectory />} />
                  <Route path="/articles/:slug" element={<ArticlePage />} />
                  <Route path="/quiz" element={<Quiz />} />
                  <Route path="/library" element={<Shop />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/lms" element={<LMS />} />
                  <Route path="/student/lms" element={<LMS />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/channeling" element={<Navigate to="/dashboard?tab=channeling" replace />} />
                  <Route path="/consultation" element={<Navigate to="/dashboard?tab=channeling" replace />} />
                  <Route path="/appointments" element={<Navigate to="/dashboard?tab=appointments" replace />} />
                  <Route path="/prescriptions" element={<Navigate to="/dashboard?tab=prescriptions" replace />} />
                  <Route path="/doctor" element={<Navigate to="/doctor/dashboard" replace />} />
                  <Route path="/doctor/*" element={<DoctorDashboard />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/admin" element={<Admin />} />
                </Routes>
              </Suspense>
            </PageTransition>
          </div>
          <Footer />
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
